/**
 * The AR invoice LIFECYCLE rules the web needs (post-on-issue,
 * 20261101120000) — one module so the panel, the grid, the dialogs and the
 * form all read the same answers:
 *
 *   draft  — books nothing, has NO number (shown as "Draft"), can be
 *            edited freely and only DELETED.
 *   Issue  — numbers the draft from the org's series, books it on the issue
 *            date and sets it `sent`.
 *   issued — cannot be deleted; date, supply date, customer, currency and AR
 *            account are locked (the fund moves via Change fund); amounts stay
 *            editable only while nothing is received.
 *   Cancel — an issued invoice with nothing received is reversed on the
 *            cancel date; a cancelled invoice is read-only.
 *
 * The SERVER is the authority for every rule here (`issue_ar_invoice`,
 * `cancel_ar_invoice`, the update / delete RPCs). These mirrors exist so the
 * UI offers only actions that can succeed and lets a date picker refuse a
 * date before the user submits it — a refusal the server still answers with
 * a code (`AR_INVOICE_ERROR_CODES`) that `arLifecycleRefusalKey` translates.
 *
 * Dates are calendar days (`YYYY-MM-DD`), compared as strings — the same
 * frame the RPCs use (`date` columns, org today from `recurring_org_today`).
 *
 * No React in this file.
 */
import { isRateEffectiveOn, isSalesTaxJurisdiction, type ARInvoiceErrorCode, type TaxRateWindow } from '@releve/shared';
import { safeFormatDate } from '@/lib/dateUtils';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** How a refusal names a date (the dialogs' and toasts' one format). */
const REFUSAL_DATE_FORMAT = 'MMM dd, yyyy';

// ---------------------------------------------------------------------------
// The number
// ---------------------------------------------------------------------------

/** i18n key of the label a draft shows where its number would be. */
export const AR_DRAFT_NUMBER_KEY = 'invoices.draft';

/**
 * The invoice number to DISPLAY: the number, or "Draft" for a draft (a NULL
 * number). Every surface that prints an AR number goes through this so a
 * draft never renders as a blank cell.
 */
export function arInvoiceNumberLabel(t: Translate, invoiceNumber: string | null | undefined): string {
  return invoiceNumber || t(AR_DRAFT_NUMBER_KEY);
}

// ---------------------------------------------------------------------------
// State predicates
// ---------------------------------------------------------------------------

interface LifecycleFacts {
  status: string | null | undefined;
  /** `'manual'` | `'channel'` | `'fund'` — only manual invoices are issued / cancelled by hand. */
  source?: string | null;
  ledger_transaction_id?: string | null;
  amount_received?: number | null;
}

/** A draft — NULL status is the DDL default `sent`, so it is never a draft. */
export function isARDraft(facts: Pick<LifecycleFacts, 'status'>): boolean {
  return facts.status === 'draft';
}

/**
 * A draft still carrying a posting from before invoices were booked on issue
 * — refused by Issue / edit / delete (`LEGACY_POSTED`) until the one-shot
 * repair (`supabase/repairs/ar_draft_postings_repair.sql`) has removed it.
 */
export function isLegacyPostedDraft(
  facts: Pick<LifecycleFacts, 'status' | 'ledger_transaction_id'>,
): boolean {
  return facts.status === 'draft' && !!facts.ledger_transaction_id;
}

/** Issued = in the books: neither a draft nor cancelled. */
export function isARIssued(facts: Pick<LifecycleFacts, 'status'>): boolean {
  return facts.status !== 'draft' && facts.status !== 'cancelled';
}

/**
 * Money received — the web's mirror of `_ar_has_receipts`, NARROWER than the
 * server rule: the server also counts a live tagged non-void journal other
 * than the posting, which the index row does not carry. An invoice with such
 * a journal and `amount_received = 0` is offered Cancel / Edit and refused
 * server-side (`HAS_PAYMENTS`, shown inline). Never treat this as the full
 * rule.
 */
export function arHasReceipts(facts: Pick<LifecycleFacts, 'amount_received'>): boolean {
  return (facts.amount_received ?? 0) > 0;
}

// ---------------------------------------------------------------------------
// The panel's action matrix
// ---------------------------------------------------------------------------

export interface ARInvoiceActionGates {
  /**
   * Edit: not cancelled, not declared / in a filed period, not a legacy-posted
   * draft, and not an issued invoice with money received (the update RPC
   * refuses every edit of it — `HAS_PAYMENTS`; the correction is a credit note
   * and a new invoice).
   */
  edit: boolean;
  /** Issue: a manual draft without a legacy posting. */
  issue: boolean;
  /** Receive payment: issued with something still owed. */
  receivePayment: boolean;
  /** Cancel: an issued manual invoice with nothing received. */
  cancel: boolean;
  /**
   * Change fund (`update_ar_invoice_fund`): an issued manual invoice with
   * nothing received — DECLARED or in a filed VAT period included, because
   * fund is not a VAT dimension (only the fund moves, never an amount, date
   * or VAT line). Never with money received: a receivable is never split
   * across two funds (`HAS_PAYMENTS`). A draft's fund is edited in the form.
   */
  changeFund: boolean;
  /** Delete: a draft without a legacy posting — issued invoices are cancelled, never deleted. */
  delete: boolean;
  /** The "repair pending" note: a legacy-posted draft offers nothing until repaired. */
  legacyPosted: boolean;
}

/**
 * Which lifecycle actions an invoice offers. `declared` = a VAT line sits on
 * a filed return (the declared-VAT guard refuses any re-post or delete), or
 * the issued invoice is dated in a filed VAT period
 * (`ar_invoice_index.in_filed_vat_period` — the update RPC refuses it with
 * `VAT_PERIOD_FILED`); either way Edit would be refused.
 * Change fund is NOT gated on `declared`: it moves the fund only, which
 * the declared-VAT guard never refuses — it is the one way to correct the
 * fund of an invoice in a filed VAT period.
 * Fund installments and channel sales are fenced server-side
 * (`FUND_MANAGED` / `CHANNEL_MANAGED`), so Issue / Cancel / Change fund are
 * manual-only.
 */
export function arInvoiceActionGates(
  facts: LifecycleFacts & {
    amount_outstanding?: number | null;
    declared: boolean;
    /** Precomputed `isLegacyPostedDraft` (the grid row carries the flag, not the journal id). */
    legacyPosted?: boolean;
  },
): ARInvoiceActionGates {
  const legacyPosted = facts.legacyPosted ?? isLegacyPostedDraft(facts);
  const manual = (facts.source ?? 'manual') === 'manual';
  const draft = isARDraft(facts);
  const issued = isARIssued(facts);
  return {
    edit:
      facts.status !== 'cancelled' &&
      !facts.declared &&
      !legacyPosted &&
      !(issued && arHasReceipts(facts)),
    issue: manual && draft && !legacyPosted,
    receivePayment: issued && facts.status !== 'paid' && (facts.amount_outstanding ?? 0) > 0,
    cancel: manual && issued && !arHasReceipts(facts),
    changeFund: manual && issued && !arHasReceipts(facts),
    delete: draft && !legacyPosted,
    legacyPosted,
  };
}

// ---------------------------------------------------------------------------
// The date rules
// ---------------------------------------------------------------------------

/** A filed VAT return's period (inclusive bounds). */
export interface FiledVatPeriod {
  period_start: string;
  period_end: string;
}

/**
 * The LIVE filed periods out of the VAT settlements list — the web mirror of
 * `_vat_period_filed` (`voided_at IS NULL AND status <> 'voided'`).
 */
export function liveFiledPeriods(
  settlements:
    | ReadonlyArray<FiledVatPeriod & { status: string; voided_at: string | null }>
    | null
    | undefined,
): FiledVatPeriod[] {
  return (settlements ?? [])
    .filter((s) => s.voided_at == null && s.status !== 'voided')
    .map((s) => ({ period_start: s.period_start.slice(0, 10), period_end: s.period_end.slice(0, 10) }));
}

/**
 * "That date falls in a VAT period you have already filed (<start> – <end>)"
 * — the one wording the Issue and Cancel dialogs show under their date
 * pickers (`invoices.lifecycle.filedPeriod`). Falls back to the bare
 * `VAT_PERIOD_FILED` message when `date` is in no listed period.
 */
export function arFiledPeriodRefusalText(
  t: Translate,
  date: string,
  periods: ReadonlyArray<FiledVatPeriod>,
): string {
  const period = filedPeriodContaining(date, periods);
  if (!period) return t('invoices.lifecycle.errors.VAT_PERIOD_FILED');
  return t('invoices.lifecycle.filedPeriod', {
    start: safeFormatDate(period.period_start, REFUSAL_DATE_FORMAT),
    end: safeFormatDate(period.period_end, REFUSAL_DATE_FORMAT),
  });
}

/** The filed period `date` falls in (inclusive), or null. */
export function filedPeriodContaining(
  date: string,
  periods: ReadonlyArray<FiledVatPeriod>,
): FiledVatPeriod | null {
  return periods.find((p) => date >= p.period_start && date <= p.period_end) ?? null;
}

/**
 * The date an Issue may not precede: the latest `invoice_date` of the org's
 * non-draft MANUAL invoices, cancelled included (`_ar_inv_series`'s
 * `last_issued_date`). Pass the ORG-WIDE index — a project-scoped list would
 * miss the org's other invoices. Null when nothing has been issued yet.
 */
export function lastIssuedInvoiceDate(
  rows: ReadonlyArray<{ source?: string | null; status: string | null; invoice_date: string | null }> | null | undefined,
): string | null {
  let last: string | null = null;
  for (const row of rows ?? []) {
    if ((row.source ?? 'manual') !== 'manual' || row.status === 'draft' || !row.invoice_date) continue;
    const day = row.invoice_date.slice(0, 10);
    if (last === null || day > last) last = day;
  }
  return last;
}

export type IssueDateRefusal = Extract<
  ARInvoiceErrorCode,
  'ISSUE_DATE_FUTURE' | 'ISSUE_DATE_BEFORE_LAST' | 'VAT_PERIOD_FILED'
>;

/**
 * The issue-date rule (`issue_ar_invoice`, checked there under the series
 * lock): on or before org today, on or after the last issued invoice's date,
 * outside a filed VAT period. Null = allowed.
 */
export function issueDateRefusal(
  date: string,
  ctx: { today: string; lastIssuedDate: string | null; filedPeriods: ReadonlyArray<FiledVatPeriod> },
): IssueDateRefusal | null {
  if (date > ctx.today) return 'ISSUE_DATE_FUTURE';
  if (ctx.lastIssuedDate && date < ctx.lastIssuedDate) return 'ISSUE_DATE_BEFORE_LAST';
  if (filedPeriodContaining(date, ctx.filedPeriods)) return 'VAT_PERIOD_FILED';
  return null;
}

export type CancelDateRefusal = Extract<
  ARInvoiceErrorCode,
  'CANCEL_DATE_FUTURE' | 'CANCEL_DATE_BEFORE_INVOICE' | 'VAT_PERIOD_FILED'
>;

/**
 * The cancel-date rule (`cancel_ar_invoice`): on or before org today, on or
 * after the invoice date, outside a filed VAT period. Null = allowed.
 */
export function cancelDateRefusal(
  date: string,
  ctx: { today: string; invoiceDate: string; filedPeriods: ReadonlyArray<FiledVatPeriod> },
): CancelDateRefusal | null {
  if (date > ctx.today) return 'CANCEL_DATE_FUTURE';
  if (date < ctx.invoiceDate.slice(0, 10)) return 'CANCEL_DATE_BEFORE_INVOICE';
  if (filedPeriodContaining(date, ctx.filedPeriods)) return 'VAT_PERIOD_FILED';
  return null;
}

// ---------------------------------------------------------------------------
// Refusal codes → copy
// ---------------------------------------------------------------------------

/**
 * The lifecycle refusal codes the web words itself (`invoices.lifecycle.errors.<code>`).
 * The two edit fences (CHANNEL_MANAGED / FUND_MANAGED) keep their own
 * `invoices.managed.*` toasts.
 */
const AR_LIFECYCLE_CODES: ReadonlySet<string> = new Set<ARInvoiceErrorCode>([
  'ACCOUNT_INVALID',
  'REASON_REQUIRED',
  'REASON_TOO_LONG',
  'NOT_POSTED',
  'NOT_AUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'LEGACY_POSTED',
  'HAS_PAYMENTS',
  'ISSUED_LOCKED',
  'CANCELLED',
  'ISSUED',
  'USE_DELETE',
  'USE_EDIT',
  'NOT_DRAFT',
  'ISSUE_DATE_FUTURE',
  'ISSUE_DATE_BEFORE_LAST',
  'CANCEL_DATE_FUTURE',
  'CANCEL_DATE_BEFORE_INVOICE',
  'VAT_PERIOD_FILED',
  'EXEMPTION_REASON_REQUIRED',
  'INVALID_STATUS',
  'INVALID_TRANSITION',
  'TAX_RATE_INVALID',
  'FUND_INVALID',
  'PROJECT_INVALID',
  'CUSTOMER_INVALID',
]);

/** The lifecycle codes, for a mutation's `meta.expectedErrorCodes` (Sentry stays quiet). */
export const AR_LIFECYCLE_ERROR_CODES: readonly string[] = [...AR_LIFECYCLE_CODES];

/** The i18n key of a lifecycle refusal code, or null when `code` is not one. */
export function arLifecycleRefusalKey(code: string | null | undefined): string | null {
  return code && AR_LIFECYCLE_CODES.has(code) ? `invoices.lifecycle.errors.${code}` : null;
}

/**
 * The dates a refusal can name — the API copies them onto the coded body
 * (`AR_DATE_RULE_DETAILS`): org `today`, the `last_issued_date` an issue may
 * not precede, the `invoice_date` a cancel may not precede.
 */
export interface ARLifecycleRefusalDetails {
  today?: string | null;
  last_issued_date?: string | null;
  invoice_date?: string | null;
  /**
   * `EXEMPTION_REASON_REQUIRED`: the line without its reason — a 1-based line
   * number (a non-numeric value, e.g. a line id, words the refusal without it).
   */
  line?: number | string | null;
}

/** i18n key of the exemption-reason refusal that names its line. */
export const AR_EXEMPTION_REASON_LINE_KEY = 'invoices.lifecycle.exemptionReasonLine';

/** A positive 1-based line number out of a refusal's `line`, or null. */
function lineNumberOf(line: number | string | null | undefined): number | null {
  const n = typeof line === 'number' ? line : typeof line === 'string' && /^\d+$/.test(line) ? Number(line) : NaN;
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * A lifecycle refusal in words, or null when `code` is not one. The date the
 * message names is the one its rule compares against.
 */
export function arLifecycleRefusalText(
  t: Translate,
  code: string | null | undefined,
  details: ARLifecycleRefusalDetails = {},
): string | null {
  const key = arLifecycleRefusalKey(code);
  if (!key) return null;
  if (code === 'EXEMPTION_REASON_REQUIRED') {
    const line = lineNumberOf(details.line);
    return line ? t(AR_EXEMPTION_REASON_LINE_KEY, { line }) : t(key);
  }
  const raw =
    code === 'ISSUE_DATE_BEFORE_LAST'
      ? details.last_issued_date
      : code === 'CANCEL_DATE_BEFORE_INVOICE'
        ? details.invoice_date
        : details.today;
  const date = raw ? safeFormatDate(String(raw).slice(0, 10), REFUSAL_DATE_FORMAT, String(raw)) : '';
  return t(key, { date });
}

// ---------------------------------------------------------------------------
// Issue pre-checks on the draft's lines
// ---------------------------------------------------------------------------

/** The line fields the Issue pre-checks read (`AR_INVOICE_LINE_ITEMS_SELECT`). */
export interface ARIssueLineFacts {
  tax_rate: number | string | null;
  tax_rate_id?: string | null;
  tax_treatment?: string | null;
  exemption_reason?: string | null;
  exemption_reason_other?: string | null;
  sort_order?: number | null;
}

/** The org rate fields the pre-checks read (`useOrganizationTaxRates`). */
export interface ARIssueRateFacts extends TaxRateWindow {
  id: string;
  rate: number | string;
  direction?: string | null;
}

/**
 * The lines in invoice order: `sort_order` (nulls last), the input order
 * breaking ties — which `useARInvoiceById` reads as `created_at, id`, the
 * RPC's own tie-break, so "line N" is the same line on both sides.
 */
function inInvoiceOrder<T extends ARIssueLineFacts>(lines: readonly T[]): T[] {
  return lines
    .map((line, index) => ({ line, index }))
    .sort((a, b) => (a.line.sort_order ?? Number.MAX_SAFE_INTEGER) - (b.line.sort_order ?? Number.MAX_SAFE_INTEGER) || a.index - b.index)
    .map(({ line }) => line);
}

const blank = (value: string | null | undefined) => !value || value.trim() === '';

/**
 * Whether a line needs an exemption reason at Issue (Clay's ruling,
 * 2026-11-02; the twin of `issue_ar_invoice`, 20261101130000): (a) its
 * treatment is `exempt`, or (b) it is not reverse-charged and its
 * `tax_rate_id` names one of the org's own OUTPUT rates at 0%. A bare
 * `zero_rated` line with no rate id (the form's "No VAT (0%)"), a plain 0%
 * line with no rate, and reverse charge need none.
 */
function needsExemptionReason(line: ARIssueLineFacts, rates: readonly ARIssueRateFacts[]): boolean {
  if (line.tax_treatment === 'exempt') return true;
  if (line.tax_treatment === 'reverse_charge' || !line.tax_rate_id) return false;
  const rate = rates.find((row) => row.id === line.tax_rate_id);
  return !!rate && rate.direction === 'OUTPUT' && Number(rate.rate) === 0;
}

/**
 * Whether the exemption-reason rule applies to an org — the SQL's gate: a
 * sales-tax org (`isSalesTaxJurisdiction`: the trimmed prefix before the
 * first `-` is `US`, so a bare `US` country counts as well as `US-VT`) is
 * never checked; every other jurisdiction, and none yet, is VAT and is.
 */
export function exemptionReasonRuleApplies(jurisdiction: string | null | undefined): boolean {
  return !isSalesTaxJurisdiction(jurisdiction);
}

/**
 * The web mirror of Issue's exemption-reason rule (VAT Directive art.
 * 226(11); NL Wet OB art. 35a(1)(l); BE KB nr. 1 art. 5 §1 9°;
 * FR CGI ann. II art. 242 nonies A I 12°): the 1-based number of the first
 * line that needs a reason (`needsExemptionReason`) and states none (none,
 * or `other` with no text), or null. Only a VAT org is checked
 * (`exemptionReasonRuleApplies` on the org's jurisdiction). A draft may stay
 * incomplete — only Issue refuses — and the RPC is the authority
 * (`EXEMPTION_REASON_REQUIRED`, with the same `line`).
 */
export function exemptionReasonMissingLine(
  lines: readonly ARIssueLineFacts[] | null | undefined,
  rates: readonly ARIssueRateFacts[] | null | undefined,
  jurisdiction: string | null | undefined,
): number | null {
  if (!exemptionReasonRuleApplies(jurisdiction)) return null;
  const ordered = inInvoiceOrder(lines ?? []);
  const index = ordered.findIndex(
    (line) =>
      needsExemptionReason(line, rates ?? []) &&
      (blank(line.exemption_reason) || (line.exemption_reason === 'other' && blank(line.exemption_reason_other))),
  );
  return index === -1 ? null : index + 1;
}

/**
 * The 1-based numbers of the lines whose rate is not in force on `ymd`
 * (`YYYY-MM-DD`) — the Issue dialog's warning when the issue date is the tax
 * point (no supply date earlier than it applies). A line without a rate, or
 * whose rate is not in the list, is skipped. A warning only: the posting
 * keeps the line's rate (docs/TAX.md "A rate outside its window").
 */
export function linesWithRateOutOfWindow(
  lines: readonly ARIssueLineFacts[] | null | undefined,
  rates: readonly ARIssueRateFacts[] | null | undefined,
  ymd: string,
): number[] {
  const out: number[] = [];
  inInvoiceOrder(lines ?? []).forEach((line, index) => {
    const rate = line.tax_rate_id ? (rates ?? []).find((row) => row.id === line.tax_rate_id) : undefined;
    if (rate && !isRateEffectiveOn(rate, ymd)) out.push(index + 1);
  });
  return out;
}
